import modelExtend from 'dva-model-extend';
import { FunctionComponent } from 'react';
import deepmerge from 'deepmerge';
import { AnyAction } from 'redux';

import personModel, { PersonFields } from './person';
import addressModel from './address';
import { PersonProps } from '../components/shared/Person/Person';
import { Field } from '../declarations/widget-ums';
import { LIVING_TOGETHER, MARITAL_STATUS } from '../constants/applicant';
import { hasErrors } from '../validators';
import { DOCUMENT_FIELD } from '../constants/document';
import applicantSerializer from '../helpers/applicantDataSerializer';
import { createField } from '../helpers/fieldHelpers';
import { differenceObject } from '../helpers/differenceObject';
import { getApplicantElkDocument } from '../selectors/elk';
import { getValue } from '../constants/getFieldValues';
import { APPLICANT } from '../constants/modelNames';

export interface ApplicantProps extends PersonProps {
  email: Field<string>;
  phone: Field<string>;
  snils: Field<string>;
  married: Field<string>;
  dateOfMarriage: Field<string>;
  marriageRecordNumber: Field<string>;
  marriageRecordHolder: Field<string>;
  livingTogether: Field<string>;
}

export type ApplicantComponent = FunctionComponent<ApplicantProps>;

export interface ApplicantFields extends PersonFields {
  phone: Field;
  livingTogether: Field<LIVING_TOGETHER | null>;
  dateOfMarriage: Field;
  marriageRecordHolder: Field;
  marriageRecordNumber: Field;
  snils: Field;
  married: Field<MARITAL_STATUS | null>;
  email: Field;
}

export const initialApplicantState: Partial<ApplicantFields> = {
  email: createField(),
  phone: createField(),
  snils: createField(),
  married: createField(null),
  dateOfMarriage: createField(),
  marriageRecordNumber: createField(),
  marriageRecordHolder: createField(),
  livingTogether: createField(null),
};

const applicantModel = modelExtend(personModel, addressModel, {
  namespace: APPLICANT,
  state: initialApplicantState,
  reducers: {
    'maritalStatus/change'(state: ApplicantFields, { payload }: AnyAction) {
      const {
        married: { value },
      } = payload;
      if (value === MARITAL_STATUS.married) {
        state.dateOfMarriage = createField();
        state.marriageRecordNumber = createField();
        state.marriageRecordHolder = createField();
      }
      state.livingTogether = createField(null);
      return Object.assign(state, payload);
    },
    'set/error'(state, { payload }) {
      return deepmerge(state, payload);
    },
  },
  effects: {
    *'maritalStatus/toggle'({ payload }, { put }) {
      yield put({ type: 'spouse/reset' });
      yield put({ type: 'maritalStatus/change', payload });
      // черновик сохраняется после эффекта applicantStatusWatcher в модели children
    },
    *'livingTogether/toggle'({ payload }, { put }) {
      yield put({ type: 'spouse/reset' });
      yield put({ type: 'onChange', payload });
      // черновик сохраняется после эффекта applicantStatusWatcher в модели children
    },
    watcher: [
      function* ({ take, put, select }) {
        const { payload } = yield take('elk/getBlocks/finish');
        const applicant = applicantSerializer(payload);

        yield put({ type: 'setElkData', payload: applicant });
        const dul = yield select(getApplicantElkDocument);
        yield put({
          type: 'document/set',
          payload: dul,
          documentField: DOCUMENT_FIELD.dul,
        });
        yield put({
          type: 'passport/validity',
        });
      },
      { type: 'watcher' },
    ],
    formErrorWatcher: [
      function* ({ take, put }) {
        while (true) {
          const {
            payload: { applicant },
          } = yield take('form/set/error');
          const applicantHasErrors = hasErrors(applicant);
          if (applicantHasErrors) {
            yield put({ type: 'set/error', payload: applicant });
          }
        }
      },
      { type: 'watcher' },
    ],
    draftWatcher: [
      function* ({ take, put, select }) {
        while (true) {
          const {
            payload: { applicant },
          } = yield take('form/set/draftData');
          const elkData = yield select((state) => state.applicant); // TODO: много лишних полей
          const {
            document,
            address,
            married,
            livingTogether,
            ...person
          } = applicant;
          const elkDocument = yield select(getApplicantElkDocument);
          const personDiff = differenceObject(elkData, person);
          const isCitizenshipChanged =
            getValue(person.citizenship) !== getValue(elkData.citizenship);
          const citizenship = isCitizenshipChanged
            ? person.citizenship
            : elkData.citizenship;

          const dul = isCitizenshipChanged && document ? document : elkDocument;

          yield put({
            type: 'setFromDraft',
            payload: { ...personDiff, citizenship, married, livingTogether },
          });
          yield put({
            type: 'document/set',
            payload: dul,
            documentField: DOCUMENT_FIELD.dul,
          });
          yield put({
            type: 'address/set/draftData',
            payload: address,
          });
          yield put({
            type: 'form/set/readyNamespace',
            payload: APPLICANT,
          });
          yield put({
            type: `${APPLICANT}/passport/validity`,
          });
        }
      },
      { type: 'watcher' },
    ],
    formResetWatcher: [
      function* ({ take, put, select }) {
        while (true) {
          yield take('form/reset');
          yield put({ type: 'applicant/reset' });
          yield put({ type: 'applicant/dul/reset' });
          yield put({ type: 'applicant/address/reset' });
          const applicant = yield select(({ elk }) => applicantSerializer(elk));
          const elkDocument = yield select(getApplicantElkDocument);
          yield put({
            type: 'setElkData',
            payload: deepmerge(initialApplicantState, applicant),
          });
          yield put({
            type: 'document/set',
            payload: elkDocument,
            documentField: DOCUMENT_FIELD.dul,
          });
        }
      },
      { type: 'watcher' },
    ],
  },
});

export default applicantModel;
